import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSEnterPhoneData,
  type EnrollmentHYCSEnterPhoneSubmitInput,
  type EnrollmentHYCSEnterPhoneSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS EnterPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSEnterPhoneData>
 * @example const response = await getEnrollmentHYCSEnterPhoneData()
 */
export const getEnrollmentHYCSEnterPhoneData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSEnterPhoneData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSEnterPhoneData',
      query: `mutation getEnrollmentHYCSEnterPhoneData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSEnterPhoneData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSEnterPhoneData = createQueryHook<undefined, EnrollmentHYCSEnterPhoneData>(
  getEnrollmentHYCSEnterPhoneData,
)

/**
 * Submit Enrollment HYCS EnterPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSEnterPhoneSubmitOutput>
 * @example const response = await submitEnrollmentHYCSEnterPhoneData();
 */
export const submitEnrollmentHYCSEnterPhoneData = async (
  enrollmentEnterPhoneSubmitInput: EnrollmentHYCSEnterPhoneSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSEnterPhoneSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSEnterPhoneData',
      query: `mutation submitEnrollmentHYCSEnterPhoneData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentEnterPhoneSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSEnterPhoneSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSEnterPhoneData = createMutationHook<
  EnrollmentHYCSEnterPhoneSubmitInput,
  EnrollmentHYCSEnterPhoneSubmitOutput
>(submitEnrollmentHYCSEnterPhoneData)
